// Same emoji set the Telegram bot parses into structured fields (location,
// deadline, target_group, format, source, ...) — those lines are kept in the
// raw description too so the post still reads like the channel post. Mapped
// to a clean icon + color so a scraped/forwarded description can be shown as
// a tidy field list instead of raw, unevenly-rendered emoji glyphs.
export const FIELD_ICON_MAP = {
  '📍': { icon: 'fa-solid fa-location-dot',    color: '#dc2626' },
  '⏳': { icon: 'fa-solid fa-hourglass-half',   color: '#ea580c' },
  '📅': { icon: 'fa-solid fa-calendar-days',    color: '#2563eb' },
  '💰': { icon: 'fa-solid fa-money-bill-wave',  color: '#16a34a' },
  '🎯': { icon: 'fa-solid fa-users',            color: '#7c3aed' },
  '👥': { icon: 'fa-solid fa-users',            color: '#7c3aed' },
  '🌐': { icon: 'fa-solid fa-globe',            color: '#0891b2' },
  '🏛': { icon: 'fa-solid fa-building',         color: '#475569' },
  '🏢': { icon: 'fa-solid fa-building',         color: '#475569' },
  '🏫': { icon: 'fa-solid fa-building',         color: '#475569' },
};

const FIELD_EMOJIS = Object.keys(FIELD_ICON_MAP);
// Matches any recognized field emoji preceded by non-newline whitespace (not at the
// very start of the string) — used to force each field onto its own line before parsing.
const FIELD_EMOJI_INLINE_RE = new RegExp(
  `[^\\S\\n]+(${FIELD_EMOJIS.map(e => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
  'g'
);
// A label word (e.g. the Armenian "Վայր" in "📍 Վայր՝ Yerevan...") sitting between the
// emoji and its separator punctuation — bounded so it can't eat real content further in.
const LEADING_LABEL_RE = /^[^:՝\-–—\n]{0,40}[:՝\-–—]\s*/;

// Splits a description into recognized emoji-prefixed field lines (rendered
// as an icon list) and the leftover freeform text. Posts often put the value
// on the line(s) *after* the emoji label ("📅 Dates:\n2026, Aug 18...") rather
// than after it on the same line, so an empty same-line value pulls in the
// plain-text lines that follow, up to the next blank line or field emoji.
//
// Some AI-scraped descriptions arrive as one flat paragraph with no real
// newlines between fields at all ("...47 ⏳ Deadline: Sept 20...") — without
// normalizing those onto their own lines first, none of them get recognized
// and the whole blob falls through to the sentence-splitting bullet fallback,
// producing garbled fragments with emoji stuck mid-sentence.
// Zero-width/format characters (seen in some Telegram-sourced posts) survive
// String.trim() since they aren't Unicode whitespace, so a line starting with one
// of these followed by a field emoji would otherwise never match FIELD_ICON_MAP.
const INVISIBLE_CHARS_RE = /[​‌‍﻿]/g;

export function extractFieldLines(text) {
  if (!text) return { fields: [], rest: '' };
  const cleaned = text.replace(INVISIBLE_CHARS_RE, '');
  const normalized = cleaned.replace(FIELD_EMOJI_INLINE_RE, '\n$1');
  const lines = normalized.split('\n');
  const consumed = new Array(lines.length).fill(false);
  const fields = [];

  lines.forEach((rawLine, i) => {
    if (consumed[i]) return;
    const line = rawLine.trim();
    if (!line) return;
    const emoji = Array.from(line)[0];
    const meta = FIELD_ICON_MAP[emoji];
    if (!meta) return;
    consumed[i] = true;

    let value = line.slice(emoji.length).replace(/^[️\s]*/, '').replace(LEADING_LABEL_RE, '').trim();
    for (let j = i + 1; j < lines.length; j++) {
      const nextLine = lines[j].trim();
      if (!nextLine) break;
      if (FIELD_ICON_MAP[Array.from(nextLine)[0]]) break;
      value = value ? `${value} ${nextLine}` : nextLine;
      consumed[j] = true;
    }
    if (value) fields.push({ ...meta, text: value });
  });

  const rest = lines.filter((_, i) => !consumed[i]).join('\n').trim();
  return { fields, rest };
}

// The modal shows recognized fields as badges already, so strip them out of
// the raw description there to avoid showing every field twice.
export function stripDuplicateFieldLines(text) {
  return extractFieldLines(text).rest;
}

// Turns the leftover free-text part of a description into a few short bullet
// points instead of one dense, line-wrapped paragraph — a single long run of
// prose is what makes a card's text block look stretched and hard to scan.
// Splitting on sentence boundaries keeps each bullet a self-contained point
// while still surfacing the same information, just as a list instead of a
// wall of text.
export function splitIntoBullets(text, { maxBullets = 3, maxCharsPerBullet = 90 } = {}) {
  if (!text) return [];
  const sentences = text
    .split(/(?<=[.!?։])\s+/)
    .map(s => s.trim())
    .filter(Boolean);
  const bullets = [];
  for (const sentence of sentences) {
    if (bullets.length >= maxBullets) break;
    bullets.push(sentence.length > maxCharsPerBullet ? `${sentence.slice(0, maxCharsPerBullet).trim()}…` : sentence);
  }
  return bullets;
}
